import type { DraftOrder } from './graphql'

type ValidationResult = {
    valid: boolean
    errors: string[]
}

export function validateB2BFields(draftOrder: DraftOrder): ValidationResult {
    const errors: string[] = []

    const purchasing = draftOrder.purchasingEntity
    const billing = draftOrder.billingAddress
    const shipping = draftOrder.shippingAddress
    const customer = draftOrder.customer
    const contact = purchasing?.contact?.customer

    if (!customer && !purchasing) {
        errors.push('A customer must be assigned to the draft order')
        return { valid: false, errors }
    }

    const firstName = contact?.firstName || billing?.firstName || shipping?.firstName || customer?.firstName
    if (!firstName?.trim()) errors.push('First name in billing address')

    const lastName = contact?.lastName || billing?.lastName || shipping?.lastName || customer?.lastName
    if (!lastName?.trim()) errors.push('Last name in billing address')

    const companyName = purchasing?.company?.name || billing?.company || shipping?.company
    if (!companyName?.trim()) errors.push('Company name in billing address')

    const email = draftOrder.email || contact?.email || customer?.email
    if (!email?.trim()) errors.push('Email in customer details')

    // Phone is intentionally NOT required: Svea treats phone as optional for B2B
    // (company) buyers, and the other BNPLX plugins never block on it (they send
    // a "0" placeholder). We still send the phone when present (extractB2BData
    // sources it, including draftOrder.phone) and fall back to a placeholder at
    // confirm. NOTE: when B2C/private-person support lands, phone becomes
    // REQUIRED for individuals — reintroduce the check for that buyer type only.

    if (!billing && !shipping) {
        errors.push('Billing address is required')
    }

    const addr = billing || shipping
    if (addr) {
        if (!addr.address1?.trim()) errors.push('Street address in billing address')
        if (!addr.city?.trim()) errors.push('City in billing address')
        if (!addr.zip?.trim()) errors.push('Postal code in billing address')
        if (!addr.countryCode?.trim()) errors.push('Country in billing address')
    }

    return { valid: errors.length === 0, errors }
}

export function extractB2BData(draftOrder: DraftOrder) {
    const purchasing = draftOrder.purchasingEntity
    const billing = draftOrder.billingAddress
    const shipping = draftOrder.shippingAddress
    const customer = draftOrder.customer
    const contact = purchasing?.contact?.customer

    return {
        email: draftOrder.email || contact?.email || customer?.email || '',
        firstName: contact?.firstName || billing?.firstName || shipping?.firstName || customer?.firstName || '',
        lastName: contact?.lastName || billing?.lastName || shipping?.lastName || customer?.lastName || '',
        companyName: purchasing?.company?.name || billing?.company || shipping?.company || '',
        phone: draftOrder.phone || contact?.phone || customer?.phone || purchasing?.location?.phone || billing?.phone || shipping?.phone || '',
        billingAddress: billing || purchasing?.location?.billingAddress || shipping,
        shippingAddress: shipping || billing,
    }
}

function mapAddress(addr: any, fallback: { firstName: string; lastName: string; companyName: string; phone: string }) {
    if (!addr) return undefined
    return {
        first_name: addr.firstName || fallback.firstName,
        last_name: addr.lastName || fallback.lastName,
        company: addr.company || fallback.companyName,
        address1: addr.address1 || '',
        address2: addr.address2 || '',
        city: addr.city || '',
        zip: addr.zip || '',
        province: addr.province || '',
        country: addr.countryCode || '',
        country_code: addr.countryCode || '',
        phone: addr.phone || fallback.phone,
    }
}

export function buildPaymentRequestBody(draftOrder: DraftOrder) {
    const b2b = extractB2BData(draftOrder)
    const total = draftOrder.totalPriceSet.shopMoney

    const mappedBilling = mapAddress(b2b.billingAddress, b2b)
    const mappedShipping = mapAddress(b2b.shippingAddress, b2b)

    return {
        email: b2b.email,
        totalPrice: total.amount,
        currency: total.currencyCode,
        draftOrderId: draftOrder.id.split('/').pop() || '',
        name: draftOrder.name,
        taxesIncluded: draftOrder.taxesIncluded,
        lineItems: draftOrder.lineItems.nodes.map(item => ({
            id: item.id.split('/').pop() || '',
            title: item.title,
            variant_title: item.variant?.title || '',
            product_id: item.product?.id?.split('/').pop() || '',
            quantity: item.quantity,
            price: item.originalUnitPriceSet.shopMoney.amount,
            tax_lines: item.taxLines.map(t => ({
                rate: t.rate,
                price: t.priceSet.shopMoney.amount,
            })),
        })),
        billingAddress: mappedBilling,
        shippingAddress: mappedShipping,
        customer: { phone: b2b.phone },
        purchasingCompany: {
            name: b2b.companyName,
            taxRegistrationId: '',
            phone: b2b.phone,
            contactPhone: b2b.phone,
        },
    }
}

export function buildConfirmBody(draftOrder: DraftOrder) {
    const b2b = extractB2BData(draftOrder)
    return {
        email: b2b.email,
        name: draftOrder.name,
        draftOrderId: draftOrder.id.split('/').pop() || '',
        shop: '',
        billingAddress: draftOrder.billingAddress,
        shippingAddress: draftOrder.shippingAddress,
        purchasingCompany: {
            name: b2b.companyName,
            taxRegistrationId: '',
            phone: b2b.phone,
            contactPhone: b2b.phone,
        },
        customer: { phone: b2b.phone },
    }
}
